import { flexRender } from '@tanstack/react-table';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { Table } from 'react-bootstrap';
import { useAdvanceTableContext } from '../../../providers/AdvanceTableProvider';

const AdvanceTable = ({
	headerClassName,
	bodyClassName,
	rowClassName,
	tableProps,
	/** 传入时拆成表头表 + 行表，仅行表交给该函数包裹（AppScrollArea 只滚行区时用） */
	renderBodyScroll,
	footerClassName,
	footerRowClassName,
	footerCells,
	footerSpanGroups,
	getBodySpanGroups,
	getRowClassName,
	getRowGroup,
}) => {
	const table = useAdvanceTableContext();
	const { getRowModel, getFlatHeaders } = table;
	const hasFooter = Boolean((footerCells && Object.keys(footerCells).length > 0) || footerSpanGroups?.length);
	const splitBodyScroll = typeof renderBodyScroll === 'function';

	const getFooterSpanGroup = (columnId) => footerSpanGroups?.find((group) => group.columnIds?.includes(columnId));
	const getBodySpanGroup = (rowOriginal, columnId) => getBodySpanGroups?.(rowOriginal)?.find((group) => group.columnIds?.includes(columnId));

	const headerRow = (
		<tr>
			{getFlatHeaders().map((header) => {
				const toggleSorting = header.column.getToggleSortingHandler();
				return (
					<th
						key={header.id}
						{...header.column.columnDef.meta?.headerProps}
						className={classNames(header.column.columnDef.meta?.headerProps?.className, {
							/* 带列筛的列：排序只在标题文字上，避免整格/箭头误触 */
							sort: header.column.getCanSort() && !header.column.columnDef.meta?.columnFilter,
							desc: header.column.getIsSorted() === 'desc' && !header.column.columnDef.meta?.columnFilter,
							asc: header.column.getIsSorted() === 'asc' && !header.column.columnDef.meta?.columnFilter,
						})}
						onClick={
							header.column.getCanSort() && !header.column.columnDef.meta?.columnFilter
								? (event) => {
										if (
											event.target.closest?.(
												'button, a, input, select, textarea, label, .order-list-column-filter-header, .order-list-column-filter-popover, .react-select__control, .react-select__menu, .react-select__menu-portal',
											)
										) {
											return;
										}
										toggleSorting?.(event);
									}
								: undefined
						}
					>
						{header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
					</th>
				);
			})}
		</tr>
	);

	let bodyRows = null;
	try {
		const rowModel = getRowModel();
		if (rowModel?.rows) {
			const columnCount = getFlatHeaders().length;
			let previousGroupKey;
			bodyRows = [];
			rowModel.rows.forEach((row, rowIndex) => {
				const group = getRowGroup?.(row.original);
				if (group?.key && group.key !== previousGroupKey) {
					previousGroupKey = group.key;
					bodyRows.push(
						<tr key={`group-${group.key}-${row.id}`} className='advance-table-row-group'>
							<td colSpan={columnCount} className='p-0'>
								{group.label}
							</td>
						</tr>,
					);
				}
				const bodySpanGroups = getBodySpanGroups?.(row.original);
				bodyRows.push(
					<tr
						key={row.id}
						className={classNames(rowClassName, getRowClassName?.(row.original), {
							'order-modal-product-subtotal-row': row.original?.rowType === 'categorySubtotal',
						})}
					>
						{row.getVisibleCells().map((cell) => {
							const rowspan = cell.column.columnDef.meta?.rowspan?.(rowIndex, rowModel.rows);
							const shouldHideCell = rowspan === 0;

							if (shouldHideCell) {
								return null;
							}

							const columnId = cell.column.id;
							const spanGroup = getBodySpanGroup(row.original, columnId);
							if (bodySpanGroups?.length && spanGroup && spanGroup.columnIds[0] !== columnId) {
								return null;
							}

							const cellProps = spanGroup?.cellProps ?? cell.column.columnDef.meta?.cellProps ?? {};
							return (
								<td
									key={cell.id}
									{...cellProps}
									colSpan={spanGroup ? spanGroup.columnIds.length : undefined}
									rowSpan={rowspan && rowspan > 1 ? rowspan : undefined}
									className={classNames(cellProps.className, {
										'border-top fw-bold align-middle': row.original?.rowType === 'categorySubtotal',
									})}
								>
									{spanGroup ? spanGroup.content : flexRender(cell.column.columnDef.cell, cell.getContext())}
								</td>
							);
						})}
					</tr>,
				);
			});
		}
	} catch {
		bodyRows = null;
	}

	const footerRow =
		hasFooter ? (
			<tr className={classNames(footerRowClassName, rowClassName)}>
				{getFlatHeaders().map((header) => {
					const columnId = header.column.id;
					const spanGroup = getFooterSpanGroup(columnId);
					if (spanGroup && spanGroup.columnIds[0] !== columnId) {
						return null;
					}

					const cellProps = spanGroup?.cellProps ?? header.column.columnDef.meta?.cellProps ?? {};
					return (
						<td
							key={`footer-${header.id}`}
							colSpan={spanGroup ? spanGroup.columnIds.length : undefined}
							{...cellProps}
							className={classNames(cellProps.className, 'border-top fw-bold align-middle')}
						>
							{spanGroup ? spanGroup.content : footerCells?.[columnId] ?? null}
						</td>
					);
				})}
			</tr>
		) : null;

	if (splitBodyScroll) {
		return (
			<>
				<div className='advance-table-head-pin'>
					<Table {...tableProps}>
						<thead className={headerClassName}>{headerRow}</thead>
					</Table>
				</div>
				{renderBodyScroll(
					<div className='advance-table-body-scroll'>
						<Table {...tableProps}>
							<tbody className={bodyClassName}>{bodyRows}</tbody>
							{footerRow ? <tfoot className={footerClassName}>{footerRow}</tfoot> : null}
						</Table>
					</div>,
				)}
			</>
		);
	}

	return (
		<div className='table-responsive scrollbar'>
			<Table {...tableProps}>
				<thead className={headerClassName}>{headerRow}</thead>
				<tbody className={bodyClassName}>{bodyRows}</tbody>
				{footerRow ? <tfoot className={footerClassName}>{footerRow}</tfoot> : null}
			</Table>
		</div>
	);
};
AdvanceTable.propTypes = {
	headerClassName: PropTypes.string,
	bodyClassName: PropTypes.string,
	rowClassName: PropTypes.string,
	tableProps: PropTypes.object,
	renderBodyScroll: PropTypes.func,
	footerClassName: PropTypes.string,
	footerRowClassName: PropTypes.string,
	footerCells: PropTypes.object,
	footerSpanGroups: PropTypes.arrayOf(
		PropTypes.shape({
			columnIds: PropTypes.arrayOf(PropTypes.string).isRequired,
			content: PropTypes.node,
			cellProps: PropTypes.object,
		}),
	),
	getBodySpanGroups: PropTypes.func,
	getRowClassName: PropTypes.func,
	getRowGroup: PropTypes.func,
};

export default AdvanceTable;
